import { useCallback, useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";
import { dictationStart, dictationApply, dictationHandover, dictationSettle, dictationText, splitSpeechResults, joinFinals, speechExtension, type DictationState, type SpeechResultEvent } from "../lib/dictation";
import { applyLiveView, type GrowLimits } from "../lib/live-view";

// ONE shared dictation engine for every mic on the site (MicButton + the inline mics on the Dream and
// astrology-ask surfaces). It keeps LISTENING until the member taps stop, auto-restarting on the browser's
// own onend (Chrome ends a session after a few seconds of silence, which read as the mic cutting off).
//
// W1-13 turned the words on: `interimResults` was false, so nothing appeared until the browser finalized
// a phrase, at a pause, and a long thought arrived in one dump. The composition law is in
// `src/lib/dictation.ts` where a GATE CAN RUN IT, and the hook takes the field's own setter so no call
// site reimplements it and none of them can drift.
//
// W4-7 finishes it, and it moved OUT of App.tsx to pay for the lines that finishing it cost (the R8
// ratchet, working as designed). The mic now carries the LIVE VIEW as well, which is what the owner
// asked for in those words: "site-wide through the shared mic component". A mic is handed its field, and
// after every repaint the field is grown and scrolled so the newest words are visible. Passing the
// field is optional, so a mic that has not been given one still dictates exactly as before rather than
// breaking.
//
// WHY THIS LIVES BESIDE ITS LAW AND NOT IN App.tsx. The pure halves (`src/lib/dictation.ts`,
// `src/lib/live-view.ts`) are already gate-RUN. This wrapper holds three hooks, so no loader can call
// it, but the checks that read it as source text (all mics take the field's own setter; the live view is
// applied where the paint happens) read it here exactly as they read it there. Nothing moved beyond the
// instrument, which is the one thing roadmap 7 must never do.
// MIC-1: `max` is the FIELD'S CAP, and it is here rather than at a call site for the reason
// `MessageComposer` already wrote down one file over — a dictation insert is one of the paths that
// arrives at full length past `maxLength`, so that component clamps inside its own `onChange`. **THE
// MIC DOES NOT GO THROUGH `onChange`.** It writes the field's setter, so without this the inbox
// composer would compose a 2,500-character message the server refuses. One clamp, in the one place
// every mic goes through, rather than a wrapper at whichever call site remembered.
/** IT #345: the recogniser errors that end dictation rather than letting `onend` restart it. */
export const MIC_TERMINAL = ["not-allowed", "service-not-allowed", "audio-capture", "network", "language-not-supported"];
export type MicTrouble = "refused" | "unavailable" | null;
export function useDictation(
  setValue: (updater: (current: string) => string) => void,
  field?: { current: { scrollHeight: number; clientHeight: number; scrollTop: number } | null },
  limits?: GrowLimits,
  max?: number,
) {
  const SR = typeof window !== "undefined"
    ? ((window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown }).SpeechRecognition
      || (window as unknown as { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition)
    : undefined;
  const [listening, setListening] = useState(false);
  // IT #345: why the mic last stopped on its own, if it did. `refused` = the phone would not let it in;
  // `unavailable` = it could not start (no mic to open, or the speech service out of reach).
  const [trouble, setTrouble] = useState<MicTrouble>(null);
  const recRef = useRef<{ stop: () => void; start: () => void } | null>(null);
  const wantRef = useRef(false); // the member's intent: keep listening until they stop
  const stateRef = useRef<DictationState>({ committed: "", live: "" });
  const setValueRef = useRef(setValue);
  setValueRef.current = setValue;
  const fieldRef = useRef(field);
  fieldRef.current = field;
  const limitsRef = useRef(limits);
  limitsRef.current = limits;
  const maxRef = useRef(max);
  maxRef.current = max;
  // Paint whatever the law says the field holds now: committed text plus the live guess.
  //
  // W4-7: then keep it visible. The live view runs in a rAF because `setValue` is a React state write
  // and the element's scrollHeight does not change until React has flushed the render. Reading it in
  // the same tick measures the PREVIOUS sentence, which is a subtle way to be one phrase behind.
  const paint = useCallback(() => {
    // The clamp is applied to what the LAW produced, not to the chunk — a cap is a fact about the
    // whole field, and clamping each arriving phrase would let a long dictation past it one phrase
    // at a time. `undefined` and a non-positive number both mean "this field has no cap", which is
    // every mic but the composer's.
    setValueRef.current(() => {
      const text = dictationText(stateRef.current);
      const cap = Number(maxRef.current);
      return cap > 0 ? text.slice(0, cap) : text;
    });
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => applyLiveView(fieldRef.current?.current as never, limitsRef.current));
    }
  }, []);

  const stop = useCallback(() => {
    wantRef.current = false;
    setListening(false);
    stateRef.current = dictationSettle(stateRef.current); // a guess the browser never finalized is still hers
    try { recRef.current?.stop(); } catch { /* noop */ }
  }, []);

  const start = useCallback(() => {
    if (!SR) return;
    // >>> **IT 163b — A SECOND `start()` ON A MIC THAT IS ALREADY LISTENING IS REFUSED.** <<< Nothing
    // reaches this twice today (`toggle` is the only caller and it guards), but `start` is RETURNED
    // from this hook, and two live recognisers writing into one `stateRef` would double every phrase
    // continuously — which is the exact symptom a member reported on 2026-09-04. A guard that costs a
    // line here is cheaper than the call site that has to remember (R31: the crossing stops existing).
    if (wantRef.current) return;
    wantRef.current = true;
    setListening(true);
    setTrouble(null);
    // Dictation opens on what is already in the box, so half-typed and half-spoken works.
    setValueRef.current((current) => { stateRef.current = dictationStart(current); return current; });
    const Recognition = SR as unknown as { new (): {
      lang: string; interimResults: boolean; continuous: boolean; onresult: (e: SpeechResultEvent) => void;
      onend: () => void; onerror: (e: { error?: string }) => void; start: () => void; stop: () => void; } };
    // `resumed` is TRUE for every recogniser but the first: a restart is where the straddle lives.
    const begin = (resumed: boolean) => {
      // IT 163b — the first FINAL a restarted recogniser produces is joined through `dictationHandover`,
      // which drops the words it echoes from the tail of what is already committed. Consumed once, so
      // a member who really repeats herself mid-sentence keeps both copies (see `src/lib/dictation.ts`).
      let handover = resumed;
      // IT 163 — **A FRESH RECOGNISER STARTS ITS RESULT LIST AT ZERO, SO THE COUNT RESETS WITH IT.**
      // The restart on a silence timeout is the whole reason dictation feels continuous, and it is
      // also where the doubling lived: carrying a count across two different result lists would skip
      // real speech, and not carrying one at all re-committed the phrase that straddled the restart.
      let through = -1;
      // IT #314: the last final this recogniser reported, so a phone's grown final keeps only its growth.
      let lastFinal = "";
      const rec = new Recognition();
      rec.lang = "en-US";
      rec.interimResults = true;
      rec.continuous = true;
      rec.onresult = (event) => {
        const { finals, live: heard, through: next } = splitSpeechResults(event, through);
        through = next;
        const grown = joinFinals(lastFinal, finals);
        lastFinal = grown.last;
        const final = grown.text;
        // The live guess on such a phone repeats the last final too, so it shows only what is new.
        const live = speechExtension(lastFinal, heard).text;
        if (final.trim()) {
          if (handover) {
            handover = false;
            stateRef.current = dictationHandover(stateRef.current, { text: final, final: true }).state;
          } else {
            stateRef.current = dictationApply(stateRef.current, { text: final, final: true });
          }
        }
        stateRef.current = dictationApply(stateRef.current, { text: live, final: false });
        paint();
      };
      // The browser ended the session (usually a silence timeout). If the member still
      // wants to dictate, seamlessly start a fresh recognizer so the paragraph continues.
      rec.onend = () => {
        // >>> **ONLY THE RECOGNISER THAT IS CURRENTLY OURS MAY RESTART THE CHAIN (IT 163b).** <<<
        // Several engines fire `onend` twice for one session, and a superseded recogniser's `onend`
        // arrives after its replacement is already running. Either way the old handler called
        // `begin()` again and left TWO live recognisers on one `stateRef` — every phrase committed
        // twice, for the rest of the dictation. That is *"its still typing double when i speak"*, and
        // `through` cannot see it at all: both lists are internally consistent.
        if (recRef.current !== rec) return;
        if (wantRef.current) { try { begin(true); } catch { setListening(false); } }
        else setListening(false);
      };
      // A permission/hardware denial is terminal; everything else (no-speech, aborted) lets onend restart.
      // IT #345: a mic that cannot be opened (`audio-capture`) or a speech service that cannot be
      // reached (`network`) is terminal too. Left to `onend` they restarted about twenty times a second
      // with the mic glowing and nothing arriving, which is "it does not work" on a phone. And every
      // terminal stop now keeps its reason, so the room can say why instead of going quiet.
      rec.onerror = (e) => {
        const why = String(e?.error || "");
        if (!MIC_TERMINAL.includes(why)) return;
        setTrouble(why === "not-allowed" || why === "service-not-allowed" ? "refused" : "unavailable");
        stop();
      };
      recRef.current = rec;
      try { rec.start(); } catch { /* a start() race just no-ops; onend will retry */ }
    };
    begin(false);
  }, [SR, stop, paint]);

  const toggle = useCallback(() => { if (wantRef.current) stop(); else start(); }, [start, stop]);

  useEffect(() => () => { wantRef.current = false; try { recRef.current?.stop(); } catch { /* noop */ } }, []);

  return { supported: !!SR, listening, toggle, start, stop, trouble };
}

// W1-13: every mic takes the FIELD'S OWN SETTER, so the live-then-committed law is applied once, in the
// hook, for all of them. It used to take an append callback and each call site wrote its own join.
// W4-7: and every mic may take the FIELD ITSELF, so the live view is applied once too.
export function MicButton({ setValue, field, limits, max }: {
  setValue: (updater: (current: string) => string) => void;
  field?: { current: HTMLTextAreaElement | HTMLInputElement | null };
  limits?: GrowLimits;
  max?: number;
}) {
  const { supported, listening, toggle } = useDictation(setValue, field as never, limits, max);
  if (!supported) return null;
  return (
    <button
      type="button"
      className={listening ? "mic-btn mic-btn--on" : "mic-btn"}
      onClick={toggle}
      aria-label={listening ? "Stop dictation" : "Dictate with your voice"}
      title={listening ? "Listening, tap to stop" : "Dictate with your voice"}
    >
      <Mic size={16} />
    </button>
  );
}
